import { useState } from "react";
import { useQuery } from "@apollo/client";
import { LinkProps } from "@generouted/react-router/client";
import Tag from "@/components/Tag";
import Button from "@/components/Button";
import type { Action } from "@/components/MenuButton";
import Table, {
  TableHeading,
  buildActionColumn,
  buildTwoLineColumn,
  buildExpandableCountColumn
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import MetricGrid from "@/components/MetricGrid";
import EmptyState from "@/components/EmptyState";
import { toFormat, isInFuture } from "@/lib/dateHelpers";
import { MaterialNames } from "@/components/material_symbol_names";
import type { Path, Params } from "@/router";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useFlags } from "@/hooks/useFeatureFlags";
import { GET_PGM_PROGRAM_TABLE_DATA_QUERY, PGMProgramTableData } from "./queries";
import { buildPATStatusForMetric } from "@/pages/education/universities/[universityId]/students/_utils/patStatus";
import {
  getStatusTagColor,
  getCompletionLabel,
  filterAndSortRequirements
} from "@/features/person/utils/pgm-requirements";
import { buildPgmUniversityMetric } from "@/features/person/utils/pgm-university-metric";
import { EducationPgmProgram, type PgmProgramRequirement } from "@/types";
import { canEditEducationRequirementType } from "@/features/person/utils/education-requirement-reason-codes";
import { deduplicateTests } from "./utils";
import DeleteProgramRequirementDialog, { type ProgramRequirementToDelete } from "./DeleteProgramRequirementDialog";
import EditEducationRequirementModal from "./EditEducationRequirementModal";
import AddEducationProgramLevelModal from "./AddEducationProgramLevelModal";
import EditEducationProgramLevelModal, { type EducationProgramLevelToEdit } from "./EditEducationProgramLevelModal";
import {
  INCOMPLETE_PGM_LEVEL_MESSAGE,
  personHasIncompletePgmLevel
} from "@/features/person/utils/education-program-level-options";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import type { TableAction } from "@/components/Table";

export interface PGMLatestProgram {
  level: string | number;
  curriculumVersion: string;
  startDate: string;
  endDate?: string | null;
  completed: boolean;
  programType: string;
  pgmProgramCompleted?: boolean;
  pgmProgramExpirationDate?: string | null;
}

export interface PGMMetric {
  label: string;
  value: string | null;
  iconName: MaterialNames;
  variant?: "success" | "warn" | "error";
  loading?: boolean;
  link?: LinkProps<Path, Params>;
  tooltip?: {
    title: string;
    supportingText: string;
    actions?: {
      primary: React.ReactNode;
    };
  };
}

type ProgramDetailType = "Requirement" | "Test";

interface ProgramDetailRow {
  id: string;
  type: ProgramDetailType;
  description: string;
  secondaryText: string;
  requireId?: string;
  levelCode: string;
  date: string | null;
  completed: boolean;
  statusLabel: string;
  statusType: "success" | "warn" | "error" | "surface" | "information";
  scoreText?: string | null;
  requirement?: PgmProgramRequirement;
  levelStartDate?: string | null;
}

interface ProgramProgressRow {
  id: string;
  level: string;
  code: string;
  version: string;
  stage: string;
  startDate: string | null;
  endDate: string | null;
  startDateIso: string;
  endDateIso: string | null;
  completeCode: string | null;
  remarks: string | null;
  detailsCount: number;
  completedDetailsCount: number;
  status: string;
  completed: boolean;
  expiresDate?: string | null;
  isExpired?: boolean;
  details: ProgramDetailRow[];
}

// Component props interface
export interface PGMProgramTableProps {
  personId: string;
  tableHeading?: string;
  tableBody?: string;
  showMetrics?: boolean;
  testId?: string;
  actions?: Array<{
    label: string;
    icon?: MaterialNames;
    variant?: "filled" | "outlined" | "text";
    onClick: () => void;
    testId?: string;
  }>;
  customMetrics?: PGMMetric[];
  context?: "education" | "membership" | "student";
  canManageEducationRequirements?: boolean;
}

// Helper functions
const isQualifyingLevel = (level: number) => level === 0;

const getProgramText = (
  programHistory: EducationPgmProgram[],
  latestProgram?: PGMLatestProgram,
  context?: "education" | "membership" | "student"
) => {
  const programType = latestProgram?.programType || programHistory[0]?.programType || "PGM";

  if (context === "student") {
    return {
      heading: "PGM Program Progress",
      body: "Program Type: Student"
    };
  } else if (context === "education") {
    return {
      heading: "PGA Professional Golf Management (PGM) History",
      body: "View PGM history including requirement statuses for each level below"
    };
  } else {
    return {
      heading: `${programType} Program`,
      body: `View ${programType.toLowerCase()} program history for the PGA Professional Golf Management (PGM)`
    };
  }
};

const formatProgramData = (programHistory: EducationPgmProgram[]): ProgramProgressRow[] => {
  return programHistory.map((level, index) => {
    const sortedReqs = level.programRequirements
      ? filterAndSortRequirements(level.programRequirements, level.levelCode)
      : [];

    const requirementRows: ProgramDetailRow[] = sortedReqs.map((req, reqIndex) => {
      const statusLabel = getCompletionLabel(req.isCompleted, req.completionStatus);
      const statusType = getStatusTagColor(statusLabel);
      return {
        id: `${index}-req-${reqIndex}`,
        type: "Requirement" as ProgramDetailType,
        description: req.requirementDefinition?.description || "Unknown requirement",
        secondaryText: req.requireId,
        requireId: req.requireId,
        levelCode: req.levelCode,
        date: req.completionDate ? toFormat(req.completionDate) : null,
        completed: req.isCompleted,
        statusLabel,
        statusType,
        scoreText: req.score != null ? `${req.score}` : null,
        requirement: req,
        levelStartDate: level.startDate
      };
    });

    const levelTests = (level.testHistory || []).filter((t) => t.levelCode === level.levelCode);
    const dedupedTests = deduplicateTests(levelTests);
    const testRows: ProgramDetailRow[] = dedupedTests.map((test, testIndex) => ({
      id: `${index}-test-${testIndex}`,
      type: "Test" as ProgramDetailType,
      description: test.testDefinition?.description || test.testId,
      secondaryText: test.testId,
      levelCode: test.levelCode,
      date: test.testDate ? toFormat(test.testDate) : null,
      completed: test.isPassed,
      statusLabel: test.isPassed ? "Passed" : test.isFailed ? "Failed" : "Not Attempted",
      statusType: test.isPassed ? "success" : test.isFailed ? "error" : "surface",
      scoreText: test.score != null ? `${test.score}/${test.maxScore}` : null
    }));

    const details = [...requirementRows, ...testRows];

    return {
      id: index.toString(),
      level: isQualifyingLevel(level.level)
        ? "Qualifying Level"
        : level.careerPath
          ? `Level ${level.level} ${level.careerPath}`
          : `Level ${level.level}`,
      code: level.levelCode,
      version: level.curriculumVersion,
      stage: level.completed ? "Completed" : "Started",
      startDate: level.startDate ? toFormat(level.startDate) : null,
      endDate: level.endDate ? toFormat(level.endDate) : null,
      startDateIso: level.startDate,
      endDateIso: level.endDate ?? null,
      completeCode: level.completeCode ?? null,
      remarks: level.remarks ?? null,
      detailsCount: details.length,
      completedDetailsCount: details.filter((d) => d.completed).length,
      status: level.completed ? "Complete" : "In Progress",
      completed: level.completed,
      expiresDate: level.levelExpirationDate ? toFormat(level.levelExpirationDate) : null,
      isExpired: level.levelExpirationDate ? !isInFuture(level.levelExpirationDate) : false,
      details
    };
  });
};

const buildLastAttendedPgmUniversityMetric = (person: PGMProgramTableData["person"]): PGMMetric | null => {
  const metric = buildPgmUniversityMetric(person);
  if (!metric) return null;
  return metric as PGMMetric;
};

// Function to build default metrics from person data
const buildDefaultMetrics = (person: PGMProgramTableData["person"]): PGMMetric[] => {
  const patStatus = buildPATStatusForMetric(person.prospectProgram?.playingAbilityTestStatus);
  const uniMetric = buildLastAttendedPgmUniversityMetric(person);

  return [
    ...(uniMetric ? [uniMetric] : []),
    {
      label: "Current Level",
      value: person.educationLatestPgmProgram ? `Level ${person.educationLatestPgmProgram.level}` : "--",
      iconName: "school" as MaterialNames
    },
    {
      label: "PGM Requirements",
      value: `${person.educationPgmProgramHistory.filter((p) => p.completed).length}/${
        person.educationPgmProgramHistory.length
      }`,
      iconName: "school" as MaterialNames,
      variant:
        person.educationPgmProgramHistory.filter((p) => p.completed).length < person.educationPgmProgramHistory.length
          ? "warn"
          : "success"
    },
    {
      label: "PAT Status",
      value: patStatus.value,
      iconName: "sports_golf" as MaterialNames,
      variant: patStatus.variant === "info" ? "warn" : patStatus.variant
    }
  ];
};

export default function PGMProgramTable(props: PGMProgramTableProps) {
  const {
    showMetrics = true,
    testId = "pgm-program-table",
    actions = [],
    customMetrics,
    context,
    personId,
    canManageEducationRequirements = false
  } = props;

  const { data, loading, error } = useQuery<PGMProgramTableData>(GET_PGM_PROGRAM_TABLE_DATA_QUERY, {
    variables: {
      pgaId: personId
    },
    fetchPolicy: "cache-first",
    skip: !personId
  });

  if (error) {
    return <EmptyState title="Error Loading Program Data" caption={error.message} iconName="error" />;
  }

  if (loading) {
    const skeletonMetrics = showMetrics
      ? customMetrics && customMetrics.length > 0
        ? customMetrics
        : [
            { label: "Current Level", value: "", iconName: "school" as MaterialNames, loading: true },
            { label: "PGM Requirements", value: "", iconName: "school" as MaterialNames, loading: true },
            { label: "PAT Status", value: "", iconName: "sports_golf" as MaterialNames, loading: true }
          ]
      : [];

    return (
      <PGMProgramTableContent
        latestProgram={undefined}
        loading={true}
        metrics={skeletonMetrics}
        tableHeading={props.tableHeading}
        tableBody={props.tableBody}
        showMetrics={showMetrics}
        testId={testId}
        actions={actions}
        tableData={[]}
        canManageEducationRequirements={canManageEducationRequirements}
        personId={personId}
        context={context}
        hasIncompleteLevel={false}
      />
    );
  }

  if (!data?.person) {
    return (
      <EmptyState
        title="No Program Data Found"
        caption="Unable to load program information for this person"
        iconName="school"
      />
    );
  }

  const person = data.person;

  const programHistory = person.educationPgmProgramHistory || [];

  const latestProgram: PGMLatestProgram | undefined = person.educationLatestPgmProgram
    ? {
        level: person.educationLatestPgmProgram.level,
        curriculumVersion: person.educationLatestPgmProgram.curriculumVersion,
        startDate: person.educationLatestPgmProgram.startDate,
        endDate: person.educationLatestPgmProgram.endDate,
        completed: person.educationLatestPgmProgram.completed,
        programType: person.educationLatestPgmProgram.programType,
        pgmProgramCompleted: person.educationLatestPgmProgram.pgmProgramCompleted,
        pgmProgramExpirationDate: person.educationLatestPgmProgram.pgmProgramExpirationDate
      }
    : undefined;

  const metrics = customMetrics || buildDefaultMetrics(person);
  const tableData = formatProgramData(programHistory);
  const hasIncompleteLevel = personHasIncompletePgmLevel(programHistory);

  const programText = getProgramText(programHistory, latestProgram, context);

  return (
    <PGMProgramTableContent
      latestProgram={latestProgram}
      loading={loading}
      metrics={metrics}
      tableHeading={programText.heading}
      tableBody={programText.body}
      showMetrics={showMetrics}
      testId={testId}
      actions={actions}
      tableData={tableData}
      canManageEducationRequirements={canManageEducationRequirements}
      personId={personId}
      context={context}
      hasIncompleteLevel={hasIncompleteLevel}
    />
  );
}

// Extracted content component to avoid duplication
interface PGMProgramTableContentProps {
  latestProgram?: PGMLatestProgram;
  loading: boolean;
  metrics: PGMMetric[];
  tableHeading?: string;
  tableBody?: string;
  showMetrics: boolean;
  testId: string;
  actions: Array<{
    label: string;
    icon?: MaterialNames;
    variant?: "filled" | "outlined" | "text";
    onClick: () => void;
    testId?: string;
  }>;
  tableData: ProgramProgressRow[];
  canManageEducationRequirements: boolean;
  personId: string;
  context?: "education" | "membership" | "student";
  hasIncompleteLevel?: boolean;
}

function PGMProgramTableContent({
  latestProgram,
  loading,
  metrics,
  tableHeading,
  tableBody,
  showMetrics,
  testId,
  actions,
  tableData,
  canManageEducationRequirements,
  personId,
  context,
  hasIncompleteLevel = false
}: PGMProgramTableContentProps) {
  const [requirementToDelete, setRequirementToDelete] = useState<ProgramRequirementToDelete | null>(null);
  const [selectedRequirement, setSelectedRequirement] = useState<{
    requirement: PgmProgramRequirement;
    levelStartDate: string;
  } | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<EducationProgramLevelToEdit | null>(null);
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const { Menu, setMenu } = useTableRowMenu();
  const flags = useFlags();
  const showEditEducationRequirementActions =
    canManageEducationRequirements && !!flags["backoffice-manage-education-requirements"];

  const openAddLevel = () => {
    if (hasIncompleteLevel) {
      showErrorSnackbar(INCOMPLETE_PGM_LEVEL_MESSAGE);
      return;
    }

    setShowAddLevelModal(true);
  };

  const headingActions: TableAction[] =
    showEditEducationRequirementActions && context === "education" && !loading
      ? [
          ...actions,
          {
            label: "Add Level",
            icon: "add",
            variant: "outlined",
            testId: "add-education-program-level-action",
            onClick: openAddLevel,
            render: () => (
              <span className={hasIncompleteLevel ? "inline-flex opacity-50" : "inline-flex"}>
                <Button
                  variant="outlined"
                  icon="add"
                  onClick={openAddLevel}
                  testId="add-education-program-level-action"
                  ariaLabel={hasIncompleteLevel ? `Add Level. ${INCOMPLETE_PGM_LEVEL_MESSAGE}` : "Add Level"}
                >
                  Add Level
                </Button>
              </span>
            )
          }
        ]
      : actions;
  // Build main table columns
  const columns = [
    buildTwoLineColumn<ProgramProgressRow>({
      accessorKey: "level",
      header: "Level",
      getPrimaryText: (row) => row.level,
      getSecondaryText: (row) => row.code,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "version",
      header: "Version",
      enableSorting: false
    },
    {
      accessorKey: "stage",
      header: "Stage",
      enableSorting: false
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      enableSorting: false
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      enableSorting: false
    },
    {
      accessorKey: "expiresDate",
      header: "Expiration",
      enableSorting: false,
      cell: ({ row }: { row: { original: ProgramProgressRow } }) => (
        <span className={row.original.isExpired ? "text-sys-brand-error" : ""}>{row.original.expiresDate || "--"}</span>
      )
    },
    buildExpandableCountColumn<ProgramProgressRow>({
      accessorKey: "requirements",
      header: "Requirements",
      getCount: (row) => row.completedDetailsCount,
      getTotal: (row) => row.detailsCount
    }),
    {
      accessorKey: "status",
      header: "Program Status",
      cell: ({ row }: { row: { original: ProgramProgressRow } }) => (
        <Tag tagType={row.original.status === "Complete" ? "success" : "warn"} size="small">
          {row.original.status}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false
    },
    ...(showEditEducationRequirementActions && context === "education"
      ? [
          buildActionColumn<ProgramProgressRow>({
            getActions: (row) => [
              {
                text: "Edit Level",
                section: "Manage",
                testId: "edit-education-program-level-action",
                onClick: () =>
                  setSelectedLevel({
                    completeCode: row.completeCode,
                    endDateIso: row.endDateIso,
                    levelCode: row.code,
                    levelLabel: row.level,
                    remarks: row.remarks,
                    startDateIso: row.startDateIso
                  })
              }
            ],
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  const detailsColumns = [
    buildTwoLineColumn<ProgramDetailRow>({
      accessorKey: "description",
      header: "Description",
      getPrimaryText: (row) => row.description,
      getSecondaryText: (row) => row.secondaryText,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => <span>{detailRow.original.type}</span>,
      enableSorting: false,
      size: 120
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.date || "--"}</span>
      ),
      enableSorting: false,
      size: 150
    },
    {
      accessorKey: "scoreText",
      header: "Score",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) =>
        detailRow.original.scoreText ? (
          <span>{detailRow.original.scoreText}</span>
        ) : (
          <span className="opacity-20">--</span>
        ),
      enableSorting: false,
      size: 100
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <Tag tagType={detailRow.original.statusType} size="small">
          {detailRow.original.statusLabel}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false,
      size: 120
    },
    ...(canManageEducationRequirements
      ? [
          buildActionColumn<ProgramDetailRow>({
            getActions: (row) => {
              if (row.type !== "Requirement" || !row.requireId) return [];

              const requireId = row.requireId;
              const requirement = row.requirement;
              const levelStartDate = row.levelStartDate || "";
              const canEditThisRequirement =
                showEditEducationRequirementActions &&
                !!requirement &&
                canEditEducationRequirementType(requirement.requirementDefinition?.requireType);

              const actions: Action[] = [];

              if (canEditThisRequirement) {
                actions.push({
                  text: "Edit Requirement",
                  section: "Manage",
                  testId: "edit-education-requirement-action",
                  onClick: () => {
                    setSelectedRequirement({ requirement, levelStartDate });
                  }
                });
              }

              actions.push({
                text: "Delete Requirement",
                iconName: "delete",
                destructive: true,
                onClick: () =>
                  setRequirementToDelete({
                    description: row.description,
                    requireId,
                    levelCode: row.levelCode
                  })
              });

              return actions;
            },
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  return (
    <>
      <Menu />
      {showMetrics && metrics.length > 0 && <MetricGrid metrics={metrics} layout="expanded" />}

      <TableHeading
        heading={tableHeading}
        body={tableBody || (latestProgram ? `Program Type: ${latestProgram.programType}` : undefined)}
        actions={headingActions}
      />

      <Table
        data={tableData}
        columns={columns}
        loading={loading}
        data-testid={testId}
        getRowCanExpand={(row) => row.original.detailsCount > 0}
        renderExpandedRow={({ row }) => {
          if (row.original.details.length > 0) {
            return (
              <TableDetailsPanel
                data={row.original.details}
                columns={detailsColumns}
                title={`Requirements & Tests (${row.original.completedDetailsCount}/${row.original.detailsCount})`}
              />
            );
          }
          return <div />;
        }}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No PGM History"
            caption="This person does not have any Professional Golf Management Program history"
            iconName="school"
          />
        )}
      />
      <DeleteProgramRequirementDialog
        open={!!requirementToDelete}
        requirement={requirementToDelete}
        pgaId={personId}
        onClose={() => setRequirementToDelete(null)}
      />
      {selectedRequirement ? (
        <EditEducationRequirementModal
          open
          onClose={() => setSelectedRequirement(null)}
          pgaId={personId}
          requirement={selectedRequirement.requirement}
          levelStartDate={selectedRequirement.levelStartDate}
        />
      ) : null}
      {showAddLevelModal ? (
        <AddEducationProgramLevelModal open onClose={() => setShowAddLevelModal(false)} pgaId={personId} />
      ) : null}
      {selectedLevel ? (
        <EditEducationProgramLevelModal
          open
          onClose={() => setSelectedLevel(null)}
          pgaId={personId}
          level={selectedLevel}
        />
      ) : null}
    </>
  );
}
